import React, { useState, useEffect, useRef } from 'react';
import { signInWithEmailAndPassword, sendPasswordResetEmail } from 'firebase/auth';
import { auth, db } from '../firebase';
import { doc, getDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import backgroundImage from '../assets/blackWall.jpg';

function Login() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const [showResetForm, setShowResetForm] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const navigate = useNavigate();
  const canvasRef = useRef(null);
  
  // Helper function to convert Firebase errors to user-friendly messages
  const getErrorMessage = (firebaseError) => {
    const errorCode = firebaseError.code;
    
    switch (errorCode) {
      case 'auth/user-not-found':
        return 'No account found with this email address.';
      case 'auth/wrong-password':
        return 'Incorrect password. Please try again.';
      case 'auth/invalid-email':
        return 'Please enter a valid email address.';
      case 'auth/user-disabled':
        return 'This account has been disabled. Please contact support.';
      case 'auth/too-many-requests':
        return 'Too many failed attempts. Please try again later.';
      case 'auth/network-request-failed':
        return 'Network error. Please check your connection and try again.';
      case 'auth/invalid-credential':
        return 'Invalid email or password. Please check your credentials.';
      default:
        return 'Login failed. Please check your credentials and try again.';
    }
  };

  // Password reset function
  const handlePasswordReset = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    
    if (!resetEmail) {
      setError('Please enter your email address.');
      return;
    }

    setIsLoading(true);
    
    try {
      await sendPasswordResetEmail(auth, resetEmail);
      setResetMessage('Password reset email sent! Check your inbox and spam folder.');
      setShowResetForm(false);
      setResetEmail('');
    } catch (err) {
      if (err.code === 'auth/user-not-found') {
        setError('No account found with this email address.');
      } else if (err.code === 'auth/invalid-email') {
        setError('Please enter a valid email address.');
      } else {
        setError('Failed to send reset email. Please try again.');
      }
    } finally {
      setIsLoading(false);
    }
  };

  // Particle logic
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    
    const ctx = canvas.getContext('2d');
    let particles = [];
    const maxTrail = window.innerWidth < 768 ? 25 : 50; // Reduce particles on mobile

    const resizeCanvas = () => {
      canvas.width = window.innerWidth;
      canvas.height = window.innerHeight;
    };
    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let mouse = { x: null, y: null };

    const handleMouseMove = (e) => {
      mouse.x = e.clientX;
      mouse.y = e.clientY;
    };

    // Only track mouse on desktop
    if (window.innerWidth >= 768) {
      document.addEventListener('mousemove', handleMouseMove);
    }

    const createTrailParticle = () => {
      if (mouse.x === null || mouse.y === null || window.innerWidth < 768) return;
      if (particles.length < maxTrail) {
        particles.push({
          x: mouse.x,
          y: mouse.y,
          size: Math.random() * 3 + 1,
          speedX: (Math.random() - 0.5) * 2,
          speedY: (Math.random() - 0.5) * 2,
          alpha: 1,
          color: 'rgba(0,255,255,',
        });
      }
    };

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      createTrailParticle();

      particles.forEach((p, i) => {
        p.x += p.speedX;
        p.y += p.speedY;
        p.alpha -= 0.015;
        if (p.alpha <= 0) {
          particles.splice(i, 1);
        } else {
          ctx.fillStyle = `${p.color}${p.alpha})`;
          ctx.beginPath();
          ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
          ctx.fill();
        }
      });

      requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      if (window.innerWidth >= 768) {
        document.removeEventListener('mousemove', handleMouseMove);
      }
    };
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setIsLoading(true);

    try {
      const userCredential = await signInWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      const userRef = doc(db, 'users', user.uid);
      const userSnap = await getDoc(userRef);

      if (userSnap.exists()) {
        const role = userSnap.data().role;

        if (role === 'admin') {
          navigate('/admin');
        } else if (role === 'client') {
          navigate('/client-dashboard');
        } else if (role === 'facebookAdmin' || role === 'facebook') {
          navigate('/facebook-events');
        } else {
          setError('Unauthorized access. Please contact support.');
        }
      } else {
        setError('Account not properly configured. Please contact support.');
      }
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container">
      <style>
        {`
          html, body {
            margin: 0;
            padding: 0;
            height: 100%;
            width: 100%;
            overflow: hidden;
            background: none;
          }

          canvas {
            display: block;
          }

          .login-container {
            position: relative;
            background-image: url(${backgroundImage});
            background-size: cover;
            background-position: center;
            margin: 0;
            padding: 0;
            min-height: 100vh;
            min-width: 100vw;
            display: flex;
            justify-content: center;
            align-items: center;
            font-family: Arial, sans-serif;
            overflow: hidden;
            padding: 1rem;
            box-sizing: border-box;
          }

          @keyframes animatedGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }

          .animated-border {
            background: linear-gradient(270deg, rgb(25, 0, 255), rgb(0, 217, 255), rgb(25, 0, 255));
            background-size: 600% 600%;
            animation: animatedGradient 8s ease infinite;
            padding: 2px;
            border-radius: 20px;
            z-index: 1;
            width: 100%;
            max-width: 500px;
          }

          .glass-inner {
            background-color: rgba(255, 255, 255, 0.1);
            backdrop-filter: blur(15px);
            -webkit-backdrop-filter: blur(15px);
            border-radius: 18px;
            padding: 2rem;
            width: 100%;
            text-align: center;
            transition: box-shadow 0.3s ease;
            box-sizing: border-box;
          }

          .glass-inner:hover {
            box-shadow: 0 0 25px rgba(0, 255, 255, 0.3);
          }

          .login-title {
            margin-bottom: 1rem;
            color: #fff;
            font-size: 2rem;
            font-weight: bold;
            line-height: 1.2;
          }

          .login-subtitle {
            margin-bottom: 1.5rem;
            color: #fff;
            font-size: 1rem;
            opacity: 0.9;
          }

          .error-message {
            color: #ff6b6b;
            margin-bottom: 1rem;
            padding: 0.75rem;
            background-color: rgba(255, 107, 107, 0.1);
            border: 1px solid rgba(255, 107, 107, 0.3);
            border-radius: 8px;
            font-size: 0.9rem;
          }

          .success-message {
            color: #4caf50;
            margin-bottom: 1rem;
            padding: 0.75rem;
            background-color: rgba(76, 175, 80, 0.1);
            border: 1px solid rgba(76, 175, 80, 0.3);
            border-radius: 8px;
            font-size: 0.9rem;
          }

          .login-input {
            width: 100%;
            padding: 0.75rem;
            margin-bottom: 1rem;
            border: 1px solid #ccc;
            border-radius: 8px;
            font-size: 1rem;
            background-color: rgba(255, 255, 255, 0.9);
            box-sizing: border-box;
            transition: border-color 0.3s ease;
          }

          .login-input:focus {
            outline: none;
            border-color: #007bff;
            box-shadow: 0 0 0 2px rgba(0, 123, 255, 0.25);
          }

          .login-button {
            width: 100%;
            padding: 0.75rem;
            background: linear-gradient(135deg, rgb(0, 0, 0), rgb(50, 50, 50));
            color: #fff;
            border: none;
            border-radius: 8px;
            font-size: 1rem;
            cursor: pointer;
            transition: all 0.3s ease;
            font-weight: 500;
            margin-bottom: 0.5rem;
          }

          .login-button:hover:not(:disabled) {
            background: linear-gradient(135deg, rgb(20, 20, 20), rgb(70, 70, 70));
            transform: translateY(-1px);
            box-shadow: 0 4px 8px rgba(0, 0, 0, 0.2);
          }

          .login-button:disabled {
            opacity: 0.6;
            cursor: not-allowed;
            transform: none;
          }

          .reset-button {
            background: none;
            border: none;
            color: #fff;
            text-decoration: underline;
            cursor: pointer;
            font-size: 0.9rem;
            margin-top: 0.5rem;
            opacity: 0.8;
            transition: opacity 0.3s ease;
          }

          .reset-button:hover {
            opacity: 1;
          }

          .back-button {
            background: none;
            border: none;
            color: #fff;
            cursor: pointer;
            font-size: 0.9rem;
            margin-bottom: 1rem;
            opacity: 0.8;
            transition: opacity 0.3s ease;
          }

          .back-button:hover {
            opacity: 1;
          }

          .reset-form {
            width: 100%;
          }

          /* Mobile Responsive Styles */
          @media (max-width: 768px) {
            .login-container {
              padding: 0.5rem;
              align-items: flex-start;
              padding-top: 10vh;
            }

            .glass-inner {
              padding: 1.5rem;
            }

            .login-title {
              font-size: 1.5rem;
              margin-bottom: 0.5rem;
            }

            .login-subtitle {
              font-size: 0.9rem;
              margin-bottom: 1rem;
            }

            .login-input {
              padding: 0.875rem;
              font-size: 16px; /* Prevents zoom on iOS */
            }

            .login-button {
              padding: 0.875rem;
              font-size: 1rem;
            }
          }

          @media (max-width: 480px) {
            .login-container {
              padding: 0.25rem;
              padding-top: 5vh;
            }

            .animated-border {
              padding: 1px;
              border-radius: 16px;
            }

            .glass-inner {
              padding: 1rem;
              border-radius: 15px;
            }

            .login-title {
              font-size: 1.25rem;
            }

            .login-subtitle {
              font-size: 0.8rem;
            }

            .error-message, .success-message {
              font-size: 0.8rem;
              padding: 0.5rem;
            }
          }

          /* Landscape mobile optimization */
          @media (max-height: 500px) and (orientation: landscape) {
            .login-container {
              padding-top: 2vh;
            }

            .glass-inner {
              padding: 1rem;
            }

            .login-title {
              font-size: 1.25rem;
              margin-bottom: 0.5rem;
            }

            .login-subtitle {
              font-size: 0.8rem;
              margin-bottom: 0.75rem;
            }
          }

          /* High DPI displays */
          @media (-webkit-min-device-pixel-ratio: 2), (min-resolution: 192dpi) {
            .glass-inner {
              backdrop-filter: blur(20px);
              -webkit-backdrop-filter: blur(20px);
            }
          }
        `}
      </style>

      {/* Canvas for animated particles */}
      <canvas
        ref={canvasRef}
        style={{
          position: 'absolute',
          top: 0,
          left: 0,
          zIndex: 0,
          pointerEvents: 'none',
        }}
      />

      <div className="animated-border">
        <div className="glass-inner">
          <h2 className="login-title">
            Strictly Results Marketing Group
          </h2>
          <h3 className="login-subtitle">
            Building Relationships. Beyond Business.
          </h3>

          {error && (
            <div className="error-message">{error}</div>
          )}

          {resetMessage && (
            <div className="success-message">{resetMessage}</div>
          )}

          {!showResetForm ? (
            <form onSubmit={handleLogin}>
              <input
                type="email"
                placeholder="Email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                className="login-input"
                disabled={isLoading}
              />
              <input
                type="password"
                placeholder="Password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                className="login-input"
                disabled={isLoading}
              />
              <button
                type="submit"
                className="login-button"
                disabled={isLoading}
              >
                {isLoading ? 'Signing in...' : 'Login'}
              </button>
              
              <button
                type="button"
                className="reset-button"
                onClick={() => {
                  setShowResetForm(true);
                  setError('');
                  setResetMessage('');
                  setResetEmail(email); // Pre-fill with login email if available
                }}
                disabled={isLoading}
              >
                Reset Password
              </button>
            </form>
          ) : (
            <div className="reset-form">
              <button
                type="button"
                className="back-button"
                onClick={() => {
                  setShowResetForm(false);
                  setError('');
                  setResetMessage('');
                  setResetEmail('');
                }}
              >
                ← Back to Login
              </button>
              
              <form onSubmit={handlePasswordReset}>
                <input
                  type="email"
                  placeholder="Enter your email address"
                  value={resetEmail}
                  onChange={(e) => setResetEmail(e.target.value)}
                  required
                  className="login-input"
                  disabled={isLoading}
                />
                <button
                  type="submit"
                  className="login-button"
                  disabled={isLoading}
                >
                  {isLoading ? 'Sending...' : 'Send Reset Email'}
                </button>
              </form>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Login;